/**
 * The client side of the Play integration skeletons that are not about money (src/client/platform/monetization.ts is):
 *  - Play Integrity: after a sign-in and before a purchase the app asks the server for a challenge
 *    (integrity.challenge), the Play Integrity API for a token of it (FwpPlay integrityToken) and gives the token to
 *    the server (integrity.verify), which records the verdict as a signal. Never blocks anything, never throws.
 *  - Diagnostics (Firebase Crashlytics and Analytics): OFF until the player turns it on in the account dialog (the
 *    choice is stored on the device); events carry no personal data (sanitizeEvent).
 *  - Push notifications (Firebase Cloud Messaging): OFF until the player turns them on (Android 13+ also asks for the
 *    system permission); the choice is stored per account on the device, so another account signing in here is not
 *    registered by it. The device's token is registered with the server (push.register) for the device session and
 *    removed when they are turned off or the player signs out; the server also drops it when that session ends in any
 *    other way (replaced by a sign-in elsewhere, revoked, a password reset).
 * Each is available only in the Android app, with its build configuration (scripts/android.mjs: the cloud project
 * number of Play Integrity, android/app/google-services.json for Firebase) and with the server's feature. Elsewhere
 * (the web page, an unconfigured build or server) every hook is a no-op and the game shows no control for it.
 */
import type { FeatureFlag, PayloadOf, RequestType, ResultOf } from '../../shared/protocol/messages';
import type { IntegrityResult, MessageLanguage } from '../../shared/protocol/model';
import type { PlayApi } from './play';

export interface IntegrationsBuildConfig {
  /** The Google Cloud project number of Play Integrity (play.json playIntegrityCloudProjectNumber); '' = off. */
  integrityCloudProject: string;
  /** The app was built with Firebase (android/app/google-services.json): Crashlytics, Analytics, Cloud Messaging. */
  firebase: boolean;
}

/** The build variables of scripts/android.mjs (playEnv). */
export function integrationsBuildConfig(env: Record<string, string | undefined>): IntegrationsBuildConfig {
  const project = (env.VITE_FWP_PLAY_INTEGRITY_PROJECT ?? '').trim();
  return { integrityCloudProject: /^\d{6,20}$/.test(project) ? project : '', firebase: env.VITE_FWP_FIREBASE === '1' };
}

/** The part of the game services these hooks use. */
export interface IntegrationServices {
  request<T extends RequestType>(type: T, payload: PayloadOf<T>): Promise<ResultOf<T>>;
  readonly serverInfo: { features: readonly FeatureFlag[] } | null;
}

export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

const has = (s: IntegrationServices, f: FeatureFlag) => !!s.serverInfo && s.serverInfo.features.includes(f);

// ------------------------------------------------------------------------------------------------ Play Integrity

export interface IntegrityHook {
  available(): boolean;
  /** One check (at most one at a time and one a minute per reason): the verdict, or null when there was none. */
  check(reason: 'sign_in' | 'purchase'): Promise<IntegrityResult | null>;
}

export function createIntegrity(o: { play: PlayApi | null; services: IntegrationServices; config: IntegrationsBuildConfig; now?: () => number }): IntegrityHook {
  const now = o.now ?? (() => Date.now());
  const last = new Map<string, number>();
  let running: Promise<IntegrityResult | null> | null = null;
  const available = () => !!o.play && o.config.integrityCloudProject !== '' && has(o.services, 'integrity');
  return {
    available,
    check(reason) {
      if (!available() || running) return running ?? Promise.resolve(null);
      if (now() - (last.get(reason) ?? -Infinity) < 60_000) return Promise.resolve(null);
      last.set(reason, now());
      running = (async () => {
        try {
          const { challenge } = await o.services.request('integrity.challenge', {});
          const { token } = await o.play!.integrityToken(o.config.integrityCloudProject, challenge);
          return await o.services.request('integrity.verify', { token, challenge, provider: 'play' });
        } catch {
          // A signal only: a failure changes nothing for the player.
          return null;
        } finally {
          running = null;
        }
      })();
      return running;
    },
  };
}

// ------------------------------------------------------------------------------------------------ diagnostics

export const TELEMETRY_KEY = 'fwp.telemetry';

/** Parameter names that could carry personal data: never sent. */
const PII_KEY = /mail|nick|name|player|user|account|token|password|phone|address|(^|_)ip($|_)|(^|_)id$/i;

/**
 * An analytics event without personal data: a lower-case name (Firebase's rules: letters, digits, _; 1..40, starting
 * with a letter), at most 10 parameters of numbers or short strings, none whose name suggests personal data. null =
 * not sent.
 */
export function sanitizeEvent(name: string, params: Record<string, unknown> = {}): { name: string; params: Record<string, string | number> } | null {
  if (!/^[a-z][a-z0-9_]{0,39}$/.test(name) || /^(firebase_|google_|ga_)/.test(name)) return null;
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (Object.keys(out).length >= 10 || !/^[a-z][a-z0-9_]{0,39}$/.test(k) || PII_KEY.test(k)) continue;
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else if (typeof v === 'string' && !v.includes('@') && v.length <= 100) out[k] = v;
  }
  return { name, params: out };
}

export interface TelemetryPort {
  /** Firebase is in this build (the account dialog shows the switch). */
  available(): boolean;
  /** The player turned diagnostics on (default off). */
  enabled(): boolean;
  setEnabled(on: boolean): Promise<void>;
  logEvent(name: string, params?: Record<string, unknown>): void;
  recordError(error: unknown): void;
}

export function createTelemetry(o: { play: PlayApi | null; config: IntegrationsBuildConfig; storage: KeyValueStore }): TelemetryPort {
  const available = () => !!o.play && o.config.firebase;
  const enabled = () => available() && o.storage.get(TELEMETRY_KEY) === '1';
  const apply = (on: boolean) => o.play!.telemetryConsent({ analytics: on, crashes: on }).catch(() => undefined);
  // The stored choice (default off) goes to the native side on every start, off included: Firebase keeps its collection
  // flags between runs, so a change that did not reach it (a failed call, lost web storage) is corrected here.
  if (available()) void apply(enabled());
  return {
    available,
    enabled,
    async setEnabled(on) {
      if (!available()) return;
      o.storage.set(TELEMETRY_KEY, on ? '1' : '0');
      await apply(on);
    },
    logEvent(name, params = {}) {
      if (!enabled()) return;
      const e = sanitizeEvent(name, params);
      if (e) void o.play!.logEvent(e.name, e.params).catch(() => undefined);
    },
    recordError(error) {
      if (!enabled()) return;
      const message = (error instanceof Error ? `${error.name}: ${error.message}` : String(error)).replace(/[\w.+-]+@[\w.-]+/g, '<email>').slice(0, 500);
      void o.play!.recordError(message).catch(() => undefined);
    },
  };
}

// ------------------------------------------------------------------------------------------------ push notifications

/** The prefix of the per-account choice (pushKeyOf). */
export const PUSH_KEY = 'fwp.push';

/** The device's stored choice of one account ('1' = on). */
export function pushKeyOf(playerId: string): string {
  return `${PUSH_KEY}.${playerId}`;
}

export interface PushPort {
  /** Cloud Messaging is in this build and the server sends notifications (the account dialog shows the switch). */
  available(): boolean;
  /** The signed-in player turned notifications on on this device (default off). */
  enabled(): boolean;
  /**
   * 'on', 'off', 'denied' (the system permission was refused) or 'unavailable' (it failed or nobody is signed in:
   * nothing changed).
   */
  setEnabled(on: boolean): Promise<'on' | 'off' | 'denied' | 'unavailable'>;
  /** After a sign-in: the device's (possibly new) token goes to the server again when notifications are on. */
  afterSignIn(): Promise<void>;
  /** Before a sign-out: this device stops getting the account's notifications (best effort). */
  beforeSignOut(): Promise<void>;
}

export function createPush(o: {
  play: PlayApi | null;
  config: IntegrationsBuildConfig;
  services: IntegrationServices;
  storage: KeyValueStore;
  lang: () => MessageLanguage;
  /** The signed-in player (null: nobody): the choice is the account's. */
  account: () => string | null;
}): PushPort {
  const available = () => !!o.play && o.config.firebase && has(o.services, 'push');
  const keyOf = () => {
    const id = o.account();
    return id ? pushKeyOf(id) : null;
  };
  const enabled = () => {
    const key = keyOf();
    return available() && key !== null && o.storage.get(key) === '1';
  };
  const register = async () => {
    const { token } = await o.play!.pushToken();
    await o.services.request('push.register', { token, platform: 'android', lang: o.lang() });
  };
  return {
    available,
    enabled,
    async setEnabled(on) {
      const key = keyOf();
      if (!available() || key === null) return 'unavailable';
      try {
        if (!on) {
          const { token } = await o.play!.pushToken().catch(() => ({ token: '' }));
          await o.services.request('push.unregister', token ? { token } : {}).catch(() => undefined);
          await o.play!.pushDeleteToken().catch(() => undefined);
          o.storage.set(key, '0');
          return 'off';
        }
        const status = await o.play!.pushRequestPermission();
        if (status.permission === 'denied') {
          o.storage.set(key, '0');
          return 'denied';
        }
        await register();
        o.storage.set(key, '1');
        // A test notification right away: the player sees that it works (and the whole chain is checked).
        void o.services.request('push.test', {}).catch(() => undefined);
        return 'on';
      } catch {
        return 'unavailable';
      }
    },
    async afterSignIn() {
      if (enabled()) await register().catch(() => undefined);
    },
    async beforeSignOut() {
      if (!enabled()) return;
      const { token } = await o.play!.pushToken().catch(() => ({ token: '' }));
      if (token) await o.services.request('push.unregister', { token }).catch(() => undefined);
    },
  };
}
